import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line, LineChart } from "recharts";
import Auth from "../Auth";
import { cn } from "src/lib/cn";
import { Text } from "src/sudokit/components/Text";
import { Spinner } from "src/sudokit/components/Spinner";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { SudoSegmentedControl } from "src/sudokit/components/SudoSegmentedControl";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { MultiCheck } from "src/sudokit/components/MultiCheck";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { Input } from "src/sudokit/components/Input";
import {
  buildChatSurveyTrends,
  isOrderedField,
  MAX_DRILL_IDS,
  measureValue,
  TREND_FIELDS,
  TREND_FIELD_IDS,
  type ChatSurveyTrendRow,
  type ChatSurveyTrends as Trends,
  type TrendFieldId,
  type TrendGroup,
  type TrendInterval,
  type TrendMeasure,
  type TrendStats,
} from "~shared/chatSurveyTrends";
import {
  ChatSurveyTrendsChart,
  DISLIKE_COLOR,
  FINE_COLOR,
  LIKE_COLOR,
  LOW_SAMPLE,
  formatMeasure,
  formatSeconds,
  type ChartPick,
} from "./ChatSurveyTrendsChart";

export type TrendsDrill = { ids: string[]; label: string };

const DAY_MS = 86_400_000;
const RANGES: Array<{ value: string; label: string; interval: TrendInterval }> = [
  { value: "today", label: "Today", interval: "hour" },
  { value: "yesterday", label: "Yesterday", interval: "hour" },
  { value: "7d", label: "Last 7 days", interval: "day" },
  { value: "4w", label: "Last 4 weeks", interval: "week" },
  { value: "custom", label: "Custom", interval: "day" },
];
const INTERVALS: Array<{ value: TrendInterval; label: string }> = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];
const MEASURES: Array<{ value: TrendMeasure; label: string }> = [
  { value: "like", label: "% Like" },
  { value: "dislike", label: "% Dislike" },
  { value: "responseMs", label: "Median response time" },
];
const SERIES_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7"];
const ORDERED_COLORS = ["#86b6ef", "#5598e7", "#2a78d6", "#1c5cab", "#104281"];
const UNKNOWN_COLOR = "#9aa0aa";
const MAX_FIELDS = 2;
const MAX_LINES = 7;
const DEFAULT_COMBINATION_LINES = 5;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type SortKey = "answers" | "like" | "fine" | "dislike" | "responseMs";
type Loaded = { rows: ChatSurveyTrendRow[]; start: number; end: number };

const card = "rounded-xl border border-solid border-app-menu-color-hover bg-app-menu-color px-5 py-4";

function dayLabel(time: number) {
  const date = new Date(time);
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

function rangeLabel(start: number, end: number) {
  return `${dayLabel(start)}–${dayLabel(end - 1)}`;
}

/** UTC window for a preset or custom range. */
function rangeWindow(range: string, now: number, custom: { from: string; to: string }) {
  const date = new Date(now);
  const today = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  let start = today, end = now;
  if (range === "yesterday") { start = today - DAY_MS; end = today; }
  else if (range === "7d") start = today - 6 * DAY_MS;
  else if (range === "4w") start = today - 27 * DAY_MS;
  else if (range === "custom") {
    start = Date.parse(`${custom.from}T00:00:00.000Z`);
    end = Math.min(now, Date.parse(`${custom.to}T00:00:00.000Z`) + DAY_MS);
  }
  return { start, end };
}

function hourLabel(time: number) {
  return `${String(new Date(time).getUTCHours()).padStart(2, "0")}:00`;
}

/** Axis label; hours carry their date when the range covers more than one day. */
function bucketLabel(time: number, interval: TrendInterval, multiDay: boolean) {
  const date = new Date(time);
  if (interval === "hour") return multiDay ? `${dayLabel(time)} ${hourLabel(time)}` : hourLabel(time);
  if (interval === "month") return `${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  return dayLabel(time);
}

function bucketTitle(time: number, interval: TrendInterval) {
  if (interval === "hour") return `${dayLabel(time)}, ${hourLabel(time)} UTC`;
  if (interval === "week") return `Week of ${dayLabel(time)}`;
  return bucketLabel(time, interval, false);
}

function changeText(delta: number, unit: "pts" | "%"): string {
  const size = Math.abs(delta).toFixed(0);
  return `${delta > 0 ? "▲" : "▼"} ${unit === "%" ? `${size}%` : `${size} percentage ${size === "1" ? "point" : "points"}`}`;
}

function isFlat(delta: number) {
  return Math.abs(delta) < 1;
}

function MixBar({ stats, frame }: { stats: TrendStats; frame: { left: number; right: number } }) {
  if (!stats.answered) return <span className="block h-2.5 w-36" />;
  const total = frame.left + frame.right;
  const share = (count: number) => (count / stats.answered) * 100;
  const dislike = share(stats.dislike), fine = share(stats.fine), like = share(stats.like);
  // Every row's "It's fine" midpoint lines up on the same center line.
  const offset = frame.left - (dislike + fine / 2);
  return (
    <span className="relative block h-2.5 w-36" aria-hidden="true">
      {[[offset, dislike, DISLIKE_COLOR], [offset + dislike, fine, FINE_COLOR], [offset + dislike + fine, like, LIKE_COLOR]].map(([left, width, color]) => (
        <span
          key={String(color)}
          className="absolute inset-y-0 rounded-sm"
          style={{ left: `${(Number(left) / total) * 100}%`, width: `calc(${(Number(width) / total) * 100}% - 2px)`, background: String(color) }}
        />
      ))}
      <span className="absolute -inset-y-1 w-px bg-app-grey-2" style={{ left: `${(frame.left / total) * 100}%` }} />
    </span>
  );
}

function mixFrame(stats: TrendStats[]) {
  let left = 0, right = 0;
  for (const s of stats) {
    if (!s.answered) continue;
    left = Math.max(left, ((s.dislike + s.fine / 2) / s.answered) * 100);
    right = Math.max(right, ((s.like + s.fine / 2) / s.answered) * 100);
  }
  return { left: left || 50, right: right || 50 };
}

function Sparkline({ values }: { values: Array<number | null> }) {
  return (
    <LineChart width={84} height={24} data={values.map((value, index) => ({ index, value }))} margin={{ top: 3, right: 3, bottom: 3, left: 3 }}>
      <Line dataKey="value" stroke="var(--grey-1)" strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
    </LineChart>
  );
}

const STAR_RULE = "Star ratings from before reactions count as 4–5★ Like, 3★ It's fine, 1–2★ Dislike.";

function Tiles({ trends, titles }: { trends: Trends; titles: string[] }) {
  const { total, points } = trends.overall;
  const tiles: Array<{ label: string; help: string; value: (s: TrendStats) => number | null; format: (v: number) => string; unit: "pts" | "%"; goodWhenUp: boolean | null }> = [
    { label: "Like", help: `Share of rated answers that were Like. ${STAR_RULE} Skipped surveys aren't counted.`, value: (s) => measureValue(s, "like"), format: (v) => formatMeasure(v, "like"), unit: "pts", goodWhenUp: true },
    { label: "It's fine", help: `Share of rated answers that were It's fine. ${STAR_RULE}`, value: (s) => measureValue(s, "fine"), format: (v) => formatMeasure(v, "fine"), unit: "pts", goodWhenUp: null },
    { label: "Dislike", help: `Share of rated answers that were Dislike. ${STAR_RULE}`, value: (s) => measureValue(s, "dislike"), format: (v) => formatMeasure(v, "dislike"), unit: "pts", goodWhenUp: false },
    { label: "Answers", help: "Rated survey answers in this range. Skipped surveys aren't counted.", value: (s) => s.answered, format: (v) => v.toLocaleString(), unit: "%", goodWhenUp: null },
  ];
  // Each card compares the latest period with the one before it, at the chosen interval.
  const last = points.length - 1;
  const latest = points[last], earlier = points[last - 1];
  const comparable = last >= 1 && latest.answered >= LOW_SAMPLE && earlier.answered >= LOW_SAMPLE;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {tiles.map((tile) => {
        const now = tile.value(total);
        // A period that is still filling in has fewer answers, so its count isn't comparable yet.
        const skipCount = tile.unit === "%" && (trends.partialLast || (trends.partialFirst && last === 1));
        const latestValue = comparable && !skipCount ? tile.value(latest) : null;
        const earlierValue = comparable && !skipCount ? tile.value(earlier) : null;
        let delta: number | null = null;
        if (latestValue != null && earlierValue != null) delta = tile.unit === "%" ? (earlierValue ? ((latestValue - earlierValue) / earlierValue) * 100 : null) : latestValue - earlierValue;
        const flat = delta != null && isFlat(delta);
        const tone = delta == null || flat || tile.goodWhenUp == null ? "text-app-grey-2" : (delta > 0) === tile.goodWhenUp ? "text-green-600" : "text-red-500";
        return (
          <div key={tile.label} className={cn(card, "flex flex-col gap-0.5 px-4 py-3")}>
            <SudoTooltip text={tile.help} position="top-start" tooltipClassName="max-w-[260px] p-2.5 text-left">
              <Text className="cursor-help text-xs text-app-grey-2">{tile.label} ⓘ</Text>
            </SudoTooltip>
            <div className="flex items-end justify-between gap-2">
              <Text className="text-2xl font-semibold text-app-grey-0">{now == null ? "—" : tile.format(now)}</Text>
              <Sparkline values={points.map(tile.value)} />
            </div>
            {delta != null && (
              <Text className={cn("text-xs font-medium", tone)}>
                {flat ? "● No change" : changeText(delta, tile.unit)}
                <span className="block font-normal text-app-grey-2">{titles[last]} vs {titles[last - 1]}</span>
              </Text>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StarsNote({ since }: { since: string }) {
  return (
    // SudoTooltip draws an inverted bubble; the content inherits its text color.
    <SudoTooltip
      position="bottom-end"
      tooltipClassName="max-w-[280px] p-3 text-left"
      customContent={(
        <div className="flex flex-col gap-1.5 text-xs">
          <span className="font-semibold">How star ratings are counted</span>
          {[["★★★★★ ★★★★", "Like", LIKE_COLOR], ["★★★", "It's fine", FINE_COLOR], ["★★ ★", "Dislike", DISLIKE_COLOR]].map(([stars, label, color]) => (
            <span key={label} className="grid grid-cols-[1fr_auto_4.5rem] items-center gap-2">
              <span>{stars}</span>
              <span aria-hidden="true" className="opacity-60">→</span>
              <span className="inline-flex items-center gap-1.5 font-semibold">
                <span className="inline-block h-2 w-2 rounded-sm" style={{ background: color }} />{label}
              </span>
            </span>
          ))}
          <span className="mt-1 opacity-75">
            Answers since {since} are reactions. The purple line marks the switch. The Responses table still shows the original stars.
          </span>
        </div>
      )}
    >
      <button type="button" className="rounded-full !bg-app-grey-4 px-3 py-1 text-xs font-medium text-app-grey-1">
        Stars before {since} ⓘ
      </button>
    </SudoTooltip>
  );
}

const SORT_COLUMNS: Array<{ key: SortKey | null; label: string }> = [
  { key: "answers", label: "Answers" },
  { key: null, label: "Mix" },
  { key: "like", label: "Like" },
  { key: "fine", label: "It's fine" },
  { key: "dislike", label: "Dislike" },
  { key: "responseMs", label: "Median response time" },
];

function sortValue(group: TrendGroup, key: SortKey): number | null {
  if (key === "answers") return group.total.answered;
  return measureValue(group.total, key);
}

function GroupTable({
  trends, field, colorOf, chartKeys, onToggleChart, sort, onSort, onSelect,
}: {
  trends: Trends;
  field: TrendFieldId | null;
  colorOf: (group: TrendGroup) => string;
  chartKeys: string[];
  onToggleChart: ((group: TrendGroup) => void) | null;
  sort: { key: SortKey | null; dir: "asc" | "desc" };
  onSort: (key: SortKey | null) => void;
  onSelect: (group: TrendGroup) => void;
}) {
  const groups = sort.key ? [...trends.groups].sort((a, b) => {
    const va = sortValue(a, sort.key as SortKey), vb = sortValue(b, sort.key as SortKey);
    if (va == null) return 1;
    if (vb == null) return -1;
    return sort.dir === "desc" ? vb - va : va - vb;
  }) : trends.groups;
  const frame = mixFrame([trends.overall.total, ...groups.map((group) => group.total)]);
  const sortable = groups.length > 1;
  const header = (key: SortKey | null, label: string, numeric: boolean) => {
    const active = key ? sort.key === key : false;
    return (
      <th
        key={label}
        scope="col"
        aria-sort={active ? (sort.dir === "desc" ? "descending" : "ascending") : undefined}
        className={cn("px-3 py-2 text-xs font-semibold uppercase tracking-wider text-app-grey-1 whitespace-nowrap", numeric ? "text-right" : "text-left")}
      >
        {key && sortable ? (
          <button type="button" onClick={() => onSort(key)} className={cn("bg-transparent p-0 text-xs font-semibold uppercase tracking-wider whitespace-nowrap", active ? "text-app-grey-0" : "text-app-grey-1")}>
            {label}{active ? (sort.dir === "desc" ? " ▼" : " ▲") : ""}
          </button>
        ) : label}
      </th>
    );
  };
  const row = (group: TrendGroup, isTotal: boolean) => {
    const onChart = chartKeys.includes(group.key);
    return (
      <tr
        key={group.key}
        tabIndex={0}
        onClick={() => onSelect(group)}
        onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onSelect(group); } }}
        className={cn("cursor-pointer border-t border-app-menu-color-hover hover:bg-app-menu-color-hover", isTotal && "font-semibold")}
      >
        {onToggleChart && (
          <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
            {!isTotal && (
              <input
                type="checkbox"
                aria-label={`Show ${group.label} on the chart`}
                checked={onChart}
                disabled={!onChart && chartKeys.length >= MAX_LINES}
                onChange={() => onToggleChart(group)}
                className="h-4 w-4 accent-app-primary"
              />
            )}
          </td>
        )}
        <td className="px-3 py-2 text-sm text-app-grey-0 whitespace-nowrap">
          <span className="inline-flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: isTotal ? "var(--grey-0)" : onChart || !field ? colorOf(group) : "var(--grey-3)" }} />
            {group.label}
            {group.total.answered < LOW_SAMPLE && <span className="rounded-full bg-app-grey-4 px-2 text-[11px] font-normal text-app-grey-2">few answers</span>}
          </span>
        </td>
        <td className="px-3 py-2 text-right text-sm tabular-nums text-app-grey-0">{group.total.answered.toLocaleString()}</td>
        <td className="px-3 py-2"><MixBar stats={group.total} frame={frame} /></td>
        {(["like", "fine", "dislike"] as const).map((key) => (
          <td key={key} className={cn("px-3 py-2 text-right text-sm tabular-nums", key === "like" ? "font-semibold text-app-grey-0" : "text-app-grey-1")}>
            {formatMeasure(measureValue(group.total, key), key)}
          </td>
        ))}
        <td className="px-3 py-2 text-right text-sm tabular-nums text-app-grey-1">{formatSeconds(measureValue(group.total, "responseMs"))}</td>
      </tr>
    );
  };
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr>
            {onToggleChart && <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-app-grey-1">Chart</th>}
            <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-app-grey-1">
              {field && sortable ? (
                <button type="button" onClick={() => onSort(null)} className="bg-transparent p-0 text-xs font-semibold uppercase tracking-wider text-app-grey-1" title="Back to the default order">
                  {TREND_FIELDS[field].label}
                </button>
              ) : field ? TREND_FIELDS[field].label : ""}
            </th>
            {SORT_COLUMNS.map((column) => header(column.key, column.label, column.key !== null))}
          </tr>
        </thead>
        <tbody>
          {field && groups.map((group) => row(group, false))}
          {row(trends.overall, true)}
        </tbody>
      </table>
    </div>
  );
}

function CombinationGrid({
  trends, totals, fields, measure, colorOf, chartKeys, onCell,
}: {
  trends: Trends;
  totals: [Trends, Trends];
  fields: [TrendFieldId, TrendFieldId];
  measure: TrendMeasure;
  colorOf: (group: TrendGroup) => string;
  chartKeys: string[];
  onCell: (group: TrendGroup, combination: boolean) => void;
}) {
  const [rowField, colField] = fields;
  const byKey = new Map(trends.groups.map((group) => [group.key, group]));
  const base = measureValue(trends.overall.total, measure);
  const higherIsBetter = measure === "like";
  const scale = measure === "responseMs" ? (base ?? 1) * 0.6 : 25;
  // Blue is better than all answers, red is worse; darker means a bigger gap.
  const fill = (value: number | null) => {
    if (value == null || base == null) return null;
    const gap = (value - base) * (higherIsBetter ? 1 : -1);
    const ratio = Math.abs(gap) / scale;
    const level = ratio < 0.1 ? 0 : ratio < 0.35 ? 1 : ratio < 0.6 ? 2 : ratio < 0.85 ? 3 : 4;
    if (!level) return { background: "var(--grey-4)" };
    const pole = gap > 0 ? LIKE_COLOR : DISLIKE_COLOR;
    return { background: `color-mix(in oklab, ${pole} ${[0, 26, 48, 70, 90][level]}%, transparent)`, color: level >= 3 ? "#ffffff" : undefined };
  };
  const sample = (stats: TrendStats) => (measure === "responseMs" ? stats.total : stats.answered);
  const cell = (group: TrendGroup | undefined, combination: boolean, emptyKey?: string) => {
    if (!group) return <td key={emptyKey} className="p-0.5"><span className="flex min-h-12 items-center justify-center text-app-grey-2">—</span></td>;
    const value = measureValue(group.total, measure);
    const low = sample(group.total) < LOW_SAMPLE;
    const onChart = combination && chartKeys.includes(group.key);
    return (
      <td key={group.key} className="p-0.5">
        <button
          type="button"
          aria-pressed={combination ? onChart : undefined}
          aria-label={`${group.label}: ${formatMeasure(value, measure)}, ${sample(group.total)} answers`}
          onClick={() => onCell(group, combination)}
          className={cn(
            "flex min-h-12 w-full min-w-[4.5rem] flex-col items-center justify-center rounded-md px-1.5 py-1 text-app-grey-0 tabular-nums",
            low && "!bg-transparent border border-dashed border-app-grey-3 text-app-grey-2",
          )}
          style={{ ...(low ? {} : fill(value)), ...(onChart ? { boxShadow: `inset 0 0 0 3px ${colorOf(group)}` } : {}) }}
        >
          <span className="text-sm font-semibold">{formatMeasure(value, measure)}</span>
          <span className="text-[10.5px] opacity-80">n={sample(group.total)}</span>
        </button>
      </td>
    );
  };
  const [rowTotals, colTotals] = totals;
  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table className="border-separate" style={{ borderSpacing: 0 }}>
          <thead>
            <tr>
              <th scope="col" className="px-2 py-1 text-left text-xs font-medium text-app-grey-2 whitespace-nowrap">
                {TREND_FIELDS[rowField].label} ↓ · {TREND_FIELDS[colField].label} →
              </th>
              {colTotals.groups.map((col) => (
                <th key={col.key} scope="col" className="px-2 py-1 text-center text-xs font-semibold text-app-grey-1 whitespace-nowrap">{col.label}</th>
              ))}
              <th scope="col" className="px-2 py-1 text-center text-xs font-semibold text-app-grey-0">All</th>
            </tr>
          </thead>
          <tbody>
            {rowTotals.groups.map((rowGroup) => (
              <tr key={rowGroup.key}>
                <th scope="row" className="px-2 py-1 pr-3 text-left text-sm font-medium text-app-grey-1 whitespace-nowrap">{rowGroup.label}</th>
                {colTotals.groups.map((col) => cell(byKey.get(`${rowGroup.key}|${col.key}`), true, `${rowGroup.key}|${col.key}`))}
                {cell(rowGroup, false)}
              </tr>
            ))}
            <tr>
              <th scope="row" className="px-2 py-1 text-left text-sm font-semibold text-app-grey-0">All</th>
              {colTotals.groups.map((col) => cell(col, false))}
              {cell(trends.overall, false)}
            </tr>
          </tbody>
        </table>
      </div>
      <Text className="text-xs text-app-grey-2">
        Each cell shows {MEASURES.find((m) => m.value === measure)?.label.toLowerCase()} for one combination. Blue is better than all answers ({formatMeasure(base, measure)}), red is worse, darker means a bigger gap.
        Dashed cells have fewer than {LOW_SAMPLE} answers. Outlined cells are on the chart, in their line's color. Click a cell to add or remove its line.
      </Text>
    </div>
  );
}

export default function ChatSurveyTrends({ onOpenResponses }: { onOpenResponses: (drill: TrendsDrill) => void }) {
  const [range, setRange] = useState("7d");
  const [custom, setCustom] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return { from: new Date(Date.now() - 27 * DAY_MS).toISOString().slice(0, 10), to: today };
  });
  const [bucketSize, setBucketSize] = useState<TrendInterval>("day");
  const [mode, setMode] = useState("all");
  const [fields, setFields] = useState<TrendFieldId[]>([]);
  const [measure, setMeasure] = useState<TrendMeasure>("like");
  const [sort, setSort] = useState<{ key: SortKey | null; dir: "asc" | "desc" }>({ key: null, dir: "desc" });
  const [picked, setPicked] = useState<string[] | null>(null);
  const [selection, setSelection] = useState<TrendsDrill | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const colorSlots = useRef(new Map<string, number>());

  useEffect(() => {
    let active = true;
    const span = rangeWindow(range, Date.now(), custom);
    if (!Number.isFinite(span.start) || !Number.isFinite(span.end) || span.end <= span.start) return;
    setLoading(true);
    setError(null);
    Auth.fetchWithIdToken("/api/admin/chat-experience-survey/trends", {
      start: new Date(span.start).toISOString(),
      end: new Date(span.end).toISOString(),
    })
      .then((result) => {
        if (!active) return;
        if (result?.success) setLoaded({ rows: result.rows, ...span });
        else setError("Couldn't load survey trends.");
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching survey trends:", err);
        setError("Couldn't load survey trends.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [range, custom]);

  const view = useMemo(() => {
    if (!loaded) return null;
    const rows = mode === "all" ? loaded.rows : loaded.rows.filter((row) => row.mode === mode);
    const { start } = loaded;
    const options = { start, end: loaded.end, previousStart: null, interval: bucketSize };
    const trends = buildChatSurveyTrends(rows, { ...options, fields });
    const totals = fields.length === 2
      ? [buildChatSurveyTrends(rows, { ...options, fields: [fields[0]] }), buildChatSurveyTrends(rows, { ...options, fields: [fields[1]] })] as [Trends, Trends]
      : null;
    const firstReaction = loaded.rows.find((row) => row.reaction)?.createdAt;
    return { trends, totals, start, end: loaded.end, firstReaction: firstReaction ? Date.parse(firstReaction) : null };
  }, [loaded, mode, bucketSize, fields]);

  const trends = view?.trends;
  const groups = trends?.groups ?? [];
  const singleOrdered = fields.length === 1 && isOrderedField(fields[0]);
  const lineLimit = fields.length === 2 ? DEFAULT_COMBINATION_LINES : MAX_LINES;
  const largest = [...groups].sort((a, b) => b.total.answered - a.total.answered).slice(0, lineLimit).map((group) => group.key);
  const chartKeys = fields.length === 0 ? [] : groups.map((group) => group.key).filter((key) => (picked ?? largest).includes(key));
  const canPickLines = fields.length === 1 && groups.length > MAX_LINES;

  // A value keeps its color while it stays on the chart.
  const slots = colorSlots.current;
  for (const key of [...slots.keys()]) if (!chartKeys.includes(key)) slots.delete(key);
  for (const key of chartKeys) {
    if (slots.has(key)) continue;
    const used = new Set(slots.values());
    let slot = 0;
    while (used.has(slot)) slot += 1;
    slots.set(key, slot);
  }
  const colorOf = (group: TrendGroup) => {
    if (group.parts.some((part) => part === "Unknown") && fields.length === 1) return UNKNOWN_COLOR;
    if (singleOrdered) return ORDERED_COLORS[(TREND_FIELDS[fields[0]] as { order: string[] }).order.indexOf(group.parts[0])] ?? UNKNOWN_COLOR;
    return SERIES_COLORS[slots.get(group.key) ?? 0] ?? UNKNOWN_COLOR;
  };

  const resetSplit = (next: TrendFieldId[]) => {
    setFields(next);
    setPicked(null);
    setSort({ key: null, dir: "desc" });
    colorSlots.current = new Map();
  };
  const toggleLine = (group: TrendGroup) => {
    const current = picked ?? chartKeys;
    if (current.includes(group.key)) setPicked(current.filter((key) => key !== group.key));
    else if (current.length < MAX_LINES) setPicked([...current, group.key]);
  };

  if (!view && loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Spinner size="lg" className="mx-auto" />
      </div>
    );
  }
  if (!view || !trends) {
    return <div className={cn(card, "text-center text-app-grey-1")}>{error ?? "No survey answers yet."}</div>;
  }

  const multiDay = view.end - view.start > DAY_MS;
  const labels = trends.buckets.map((time) => bucketLabel(time, bucketSize, multiDay));
  const titles = trends.buckets.map((time, index) => {
    const base = bucketTitle(time, bucketSize);
    if (trends.partialLast && index === trends.buckets.length - 1) return `${base} (so far)`;
    return trends.partialFirst && index === 0 ? `${base} (partial)` : base;
  });
  const fromStars = trends.overall.total.fromStars;
  const hasStars = fromStars.like + fromStars.fine + fromStars.dislike > 0;
  const markerIndex = hasStars && view.firstReaction != null
    ? trends.buckets.findIndex((time, index) => view.firstReaction! >= time && view.firstReaction! < (trends.buckets[index + 1] ?? Infinity))
    : -1;
  const switchDate = view.firstReaction != null ? dayLabel(view.firstReaction) : null;
  const lines = fields.length ? groups.filter((group) => chartKeys.includes(group.key)).map((group) => ({ group, color: colorOf(group) })) : null;
  const rangeText = rangeLabel(view.start, view.end);
  const modeText = mode === "all" ? "" : ` · ${mode === "write" ? "Write" : "Chat"} mode`;

  const select = (label: string, ids: string[]) => setSelection({ label: `${label}${modeText}`, ids });
  const onPick = ({ index, group }: ChartPick) => {
    const stats = group ? group.points[index] : trends.overall.points[index];
    select(group ? `${titles[index]} · ${group.label}` : titles[index], stats.answeredIds);
  };

  // Says what the y-axis is, since it changes with the measure.
  const per = INTERVALS.find((option) => option.value === bucketSize)?.label.toLowerCase();
  const chartCaption = !fields.length
    ? `Share of rated answers per ${per}: Like, It's fine and Dislike`
    : `${measure === "responseMs" ? "Median response time" : `${MEASURES.find((m) => m.value === measure)?.label} of rated answers`} per ${per}, one line per ${fields.map((field) => TREND_FIELDS[field].label).join(" · ")}`;
  const notes: string[] = [];
  if (!fields.length) notes.push("Like above the line, Dislike below, It's fine across the middle.");
  else {
    notes.push(`Hollow points have fewer than ${LOW_SAMPLE} answers.`);
    if (chartKeys.length < groups.length) {
      notes.push(fields.length === 2
        ? `Showing ${chartKeys.length} of ${groups.length} combinations. Click cells in the grid to add or remove lines (up to ${MAX_LINES}).`
        : `Showing ${chartKeys.length} of ${groups.length} values. Tick rows in the table to change which.`);
    }
  }
  if (trends.partialFirst && trends.partialLast) notes.push("The first period starts before the range and the last is still filling in, so both are drawn dashed or faded.");
  else if (trends.partialFirst) notes.push("The first period starts before the range, so it's drawn dashed or faded.");
  else if (trends.partialLast) notes.push("The last period is still filling in, so it's drawn dashed or faded.");
  if (markerIndex >= 0) notes.push("The purple line marks the switch from stars to reactions.");

  return (
    <div className={cn("flex flex-col gap-5 pb-24 transition-opacity", loading && "opacity-60")}>
      {error && (
        <div className="rounded-xl border border-solid border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-500" role="alert">
          {error} The numbers below are from the previous range.
        </div>
      )}
      <div className="flex flex-wrap items-end gap-4 border-b border-app-menu-color-hover pb-4">
        <MenuSelect
          label="Date range"
          value={range}
          onChange={(value: string) => {
            setRange(value);
            setBucketSize(RANGES.find((option) => option.value === value)?.interval ?? "day");
            setSelection(null);
          }}
          options={RANGES.map(({ value, label }) => ({ value, label }))}
          placeholder="Date range"
        />
        {range === "custom" && (
          <div className="flex items-center gap-2">
            <Input aria-label="From date" type="date" mode="default" value={custom.from} max={custom.to}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => event.target.value && setCustom((current) => ({ ...current, from: event.target.value }))} className="w-36 text-sm" />
            <Text className="text-sm text-app-grey-1">to</Text>
            <Input aria-label="To date" type="date" mode="default" value={custom.to} min={custom.from}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => event.target.value && setCustom((current) => ({ ...current, to: event.target.value }))} className="w-36 text-sm" />
          </div>
        )}
        <SudoSegmentedControl
          aria-label="Interval"
          size="small"
          value={bucketSize}
          onChange={(value) => setBucketSize(value as TrendInterval)}
          options={INTERVALS}
        />
        <MenuSelect
          label="Mode"
          value={mode}
          onChange={(value: string) => { setMode(value); setSelection(null); }}
          options={[{ value: "all", label: "All modes" }, { value: "write", label: "Write" }, { value: "chat", label: "Chat" }]}
          placeholder="Mode"
        />
        <Text className="text-xs text-app-grey-2">{rangeText} · UTC</Text>
      </div>

      <Tiles trends={trends} titles={titles} />

      <section className={cn(card, "flex flex-col gap-3")} aria-label="Survey trend chart">
        <div className="flex flex-wrap items-center gap-3">
          <Text className="text-sm text-app-grey-1">Split by</Text>
          {/* MultiCheck's labels are page-sized; shrink them to match the other controls here. */}
          <MultiCheck
            className="gap-1.5 [&_.text-base]:!text-sm"
            options={TREND_FIELD_IDS.map((id) => ({ value: id, label: TREND_FIELDS[id].label }))}
            value={fields}
            onChange={(next: string[]) => { if (next.length <= MAX_FIELDS) resetSplit(next as TrendFieldId[]); }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {fields.length > 0 ? (
            <>
              <Text className="text-sm text-app-grey-1">Measure</Text>
              <SudoSegmentedControl aria-label="Measure" size="small" value={measure} onChange={(value) => setMeasure(value as TrendMeasure)} options={MEASURES} />
            </>
          ) : (
            <Text className="text-xs text-app-grey-2">Pick up to two fields. Two fields split by every combination.</Text>
          )}
          {hasStars && switchDate && <div className="ml-auto"><StarsNote since={switchDate} /></div>}
        </div>
        <Text className="text-sm font-medium text-app-grey-0">{chartCaption}</Text>
        {lines && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-app-grey-1">
            {lines.map(({ group, color }) => (
              <span key={group.key} className="inline-flex items-center gap-1.5"><span className="inline-block h-0.5 w-3.5 rounded" style={{ background: color }} />{group.label}</span>
            ))}
          </div>
        )}
        {trends.overall.total.answered === 0 ? (
          <Text className="py-16 text-center text-app-grey-1">No rated answers in this range. Skipped surveys aren't counted.</Text>
        ) : (
          <ChatSurveyTrendsChart
            trends={trends}
            labels={labels}
            titles={titles}
            measure={measure}
            lines={lines}
            marker={markerIndex >= 0 ? { index: markerIndex } : null}
            onPick={onPick}
          />
        )}
        <Text className="text-xs text-app-grey-2">{notes.join(" ")}</Text>
      </section>

      <section className={cn(card, "flex flex-col gap-3")} aria-label="Survey breakdown">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <Text className="text-base font-semibold text-app-grey-0">
            {fields.length === 2 ? `${TREND_FIELDS[fields[0]].label} × ${TREND_FIELDS[fields[1]].label}` : fields.length ? `By ${TREND_FIELDS[fields[0]].label.toLowerCase()}` : "All answers"}
          </Text>
          <Text className="text-xs text-app-grey-2">
            Covers {rangeText}. {fields.length === 2 ? "Click a cell to select its responses." : "Click a header to sort, a row to select its responses."}
          </Text>
        </div>
        {fields.length === 2 && view.totals ? (
          <CombinationGrid
            trends={trends}
            totals={view.totals}
            fields={fields as [TrendFieldId, TrendFieldId]}
            measure={measure}
            colorOf={colorOf}
            chartKeys={chartKeys}
            onCell={(group, combination) => {
              if (combination) toggleLine(group);
              select(`${rangeText} · ${group.label}`, group.total.answeredIds);
            }}
          />
        ) : (
          <GroupTable
            trends={trends}
            field={fields[0] ?? null}
            colorOf={colorOf}
            chartKeys={chartKeys}
            onToggleChart={canPickLines ? toggleLine : null}
            sort={sort}
            onSort={(key) => setSort((current) => (key === null ? { key: null, dir: "desc" } : { key, dir: current.key === key && current.dir === "desc" ? "asc" : "desc" }))}
            onSelect={(group) => select(group.key === "all" ? rangeText : `${rangeText} · ${group.label}`, group.total.answeredIds)}
          />
        )}
      </section>

      {selection && (
        <div className={cn(card, "sticky bottom-4 z-10 flex flex-wrap items-center gap-3 self-center py-2.5 text-sm text-app-grey-0 shadow-card")} role="status">
          <span className="font-semibold">{selection.ids.length.toLocaleString()} responses</span>
          <span className="text-app-grey-1">{selection.label}</span>
          {selection.ids.length > MAX_DRILL_IDS ? (
            <span className="text-xs text-app-grey-2">Too many to list at once (max {MAX_DRILL_IDS.toLocaleString()}). Pick a smaller slice or range.</span>
          ) : (
            <SudoButton type="button" variant="primary" size="small" disabled={!selection.ids.length} onClick={() => onOpenResponses(selection)}>
              Open in Responses
            </SudoButton>
          )}
          <button type="button" aria-label="Clear selection" onClick={() => setSelection(null)} className="!bg-transparent px-1 text-app-grey-1">✕</button>
        </div>
      )}
    </div>
  );
}
